import React, { useState, useEffect } from "react";
import { createAppointment } from '../../Services/appointmentService';
import Swal from "sweetalert2";
import { useMasterData } from "../Common/commonEvents";

export default function AppointmentModal({ show, onClose, onSuccess }) {

    const [appointmentType, setAppointmentType] = useState("NEW");
    const [formData, setFormData] = useState({
        name: "",
        mobile: "",
        email: "",
        age: "",
        dob: "",
        gender: "1",
        address: "",
        unitId: "1",
        appointmentDate: "",
        notes: "",
        allergy: "",
        bp: "",
        weight: "",
        diabetes: false,
        epilepsy: false,
        asthma: false,
        otherConditions: "",
        medications: "",
        slotTime: "",
        doctorId: "",
        procedureId: "",
    });

    const sessionUserData = JSON.parse(sessionStorage.getItem("userData") || "{}");

    const [loading, setLoading] = useState(false);

    const {
        procedures,
        fetchProcedureMasters,
    } = useMasterData();

    useEffect(() => {
        if (!show) return;
        if (!sessionUserData?.OrgId || !sessionUserData?.UnitId) return;

        fetchProcedureMasters(sessionUserData.OrgId, sessionUserData.UnitId);
    }, [show, sessionUserData?.OrgId, sessionUserData?.UnitId, fetchProcedureMasters]);

    if (!show) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const Toast = Swal.mixin({
        toast: true,
        position: "top",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        const basePayload = {
            OrgId: sessionUserData?.OrgId,
            UserId: sessionUserData?.Id,
            Type: appointmentType,
        };

        const payload =
            appointmentType === "EXISTING"
                ? {
                    ...basePayload,
                    JsonData: {
                        Mobile: formData.mobile,
                        UnitId: Number(formData.unitId) || 1,
                        AppointmentDate: formData.appointmentDate,
                        SlotTime: formData.slotTime,
                        DoctorId: formData.doctorId,
                        ProcedureId: formData.procedureId,
                    },
                }
                : {
                    ...basePayload,
                    JsonData: {
                        Name: formData.name,
                        Mobile: formData.mobile,
                        Email: formData.email,
                        DOB: formData.dob,
                        Age: Number(formData.age) || 0,
                        Gender: Number(formData.gender) || 1,
                        Address: formData.address,
                        UnitId: Number(formData.unitId) || 1,
                        AppointmentDate: formData.appointmentDate,
                        Notes: formData.notes,
                        Allergy: formData.allergy || "None",
                        BP: formData.bp,
                        Weight: formData.weight,
                        Diabetes: formData.diabetes ? 1 : 0,
                        Epilepsy: formData.epilepsy ? 1 : 0,
                        Asthma: formData.asthma ? 1 : 0,
                        OtherConditions: formData.otherConditions,
                        Medications: formData.medications,
                        SlotTime: formData.slotTime,
                        ProcedureId: formData.procedureId,
                    },
                };

        try {
            setLoading(true);

            const response = await createAppointment(payload);

            const result = response?.data?.result?.[0];

            if (response?.success && result?.ResponseCode === 2001) {
                Toast.fire({
                    icon: "success",
                    title: result?.Message || "Appointment created successfully",
                });

                onSuccess?.();
                onClose();
            } else {
                Toast.fire({
                    icon: "error",
                    title: result?.Message || response?.message || "Failed to create appointment",
                });
            }
        } catch (error) {
            console.error(error.response?.data || error.message);

            Toast.fire({
                icon: "error",
                title:
                    error.response?.data?.message ||
                    "Failed to create appointment",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="modal show d-block"
            style={{
                background: "rgba(15, 23, 42, 0.62)",
                backdropFilter: "blur(6px)",
                overflowY: "auto",
                padding: "24px 12px",
            }}
        >
            <div
                className="modal-dialog modal-xl modal-dialog-centered"
                style={{
                    maxHeight: "calc(100vh - 48px)",
                    margin: "0 auto",
                }}
            >
                <div
                    className="modal-content border-0 shadow-lg overflow-hidden"
                    style={{
                        borderRadius: "22px",
                        maxHeight: "calc(100vh - 48px)",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div
                        className="modal-header border-0 px-4 py-4"
                        style={{
                            background: "linear-gradient(135deg, #0f766e 0%, #2563eb 100%)",
                        }}
                    >
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className="rounded-circle bg-white bg-opacity-25 d-flex align-items-center justify-content-center"
                                style={{ width: "52px", height: "52px" }}
                            >
                                <i className="bi bi-calendar2-plus text-white fs-3"></i>
                            </div>

                            <div>
                                <h5 className="modal-title fw-bold text-white mb-1">
                                    Make Appointment
                                </h5>
                                <div className="text-white text-opacity-75 fs-7">
                                    Add patient details and schedule slot
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn btn-sm rounded-circle bg-white bg-opacity-25 border-0 text-white"
                            onClick={onClose}
                            style={{ width: "38px", height: "38px" }}
                        >
                            <i className="bi bi-x-lg text-white"></i>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <div
                            className="modal-body p-4 bg-light"
                            style={{
                                overflowY: "auto",
                                flex: "1 1 auto",
                            }}
                        >
                            <div className="bg-white rounded-4 p-3 shadow-sm mb-4">
                                <label className="form-label fw-bold text-dark mb-2">Patient Type</label>

                                <div className="d-flex gap-3 flex-wrap">
                                    <label
                                        className={`border rounded-4 px-4 py-3 d-flex align-items-center gap-3 ${appointmentType === "NEW"
                                            ? "border-primary bg-primary bg-opacity-10"
                                            : "bg-light border-light"
                                            }`}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <input
                                            type="radio"
                                            name="appointmentType"
                                            value="NEW"
                                            checked={appointmentType === "NEW"}
                                            onChange={(e) => setAppointmentType(e.target.value)}
                                            className="form-check-input m-0"
                                        />
                                        <i className="bi bi-person-plus text-primary fs-5"></i>
                                        <span className="fw-bold">New Patient</span>
                                    </label>

                                    <label
                                        className={`border rounded-4 px-4 py-3 d-flex align-items-center gap-3 ${appointmentType === "EXISTING"
                                            ? "border-success bg-success bg-opacity-10"
                                            : "bg-light border-light"
                                            }`}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <input
                                            type="radio"
                                            name="appointmentType"
                                            value="EXISTING"
                                            checked={appointmentType === "EXISTING"}
                                            onChange={(e) => setAppointmentType(e.target.value)}
                                            className="form-check-input m-0"
                                        />
                                        <i className="bi bi-person-check text-success fs-5"></i>
                                        <span className="fw-bold">Existing Patient</span>
                                    </label>
                                </div>
                            </div>

                            <div className="bg-white rounded-4 p-4 shadow-sm mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="bi bi-person-vcard text-primary fs-5"></i>
                                    <h6 className="fw-bold mb-0 text-dark">
                                        {appointmentType === "NEW" ? "Patient Information" : "Existing Patient"}
                                    </h6>
                                </div>
                                <div className="row g-3">
                                    {appointmentType === "NEW" && (
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold text-muted">Name</label>
                                            <input
                                                name="name"
                                                className="form-control form-control-sm rounded-3 border-light bg-light"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Enter patient name"
                                                required
                                            />
                                        </div>
                                    )}
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Mobile</label>
                                        <input
                                            name="mobile"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.mobile}
                                            onChange={handleChange}
                                            placeholder="Enter mobile number"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Enter email address"
                                        />
                                    </div>
                                    {appointmentType === "NEW" && (
                                        <>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold text-muted">Age</label>
                                                <input
                                                    type="number"
                                                    name="age"
                                                    className="form-control form-control-sm rounded-3 border-light bg-light"
                                                    value={formData.age}
                                                    onChange={handleChange}
                                                    placeholder="Age"
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold text-muted">Gender</label>
                                                <select
                                                    name="gender"
                                                    className="form-select form-select-sm rounded-3 border-light bg-light"
                                                    value={formData.gender}
                                                    onChange={handleChange}
                                                >
                                                    <option value="1">Male</option>
                                                    <option value="2">Female</option>
                                                    <option value="3">Other</option>
                                                </select>
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold text-muted">DOB</label>
                                                <input
                                                    type="date"
                                                    name="dob"
                                                    className="form-control form-control-sm rounded-3 border-light bg-light"
                                                    value={formData.dob}
                                                    onChange={handleChange}
                                                    // max 1 year past from today
                                                    max={
                                                        new Date(
                                                            new Date().setFullYear(
                                                                new Date().getFullYear() - 1
                                                            )
                                                        )
                                                            .toISOString()
                                                            .split("T")[0]
                                                    }
                                                />
                                            </div>

                                            <div className="col-md-12">
                                                <label className="form-label fw-semibold text-muted">Address</label>
                                                <textarea
                                                    name="address"
                                                    className="form-control rounded-3 border-light bg-light"
                                                    rows="2"
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                    placeholder="Enter address"
                                                ></textarea>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white rounded-4 p-4 shadow-sm mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="bi bi-calendar-check text-success fs-5"></i>
                                    <h6 className="fw-bold mb-0 text-dark">Appointment Details</h6>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Procedure</label>
                                        <select
                                            className="form-select form-select-sm"
                                            name="procedureId"
                                            value={formData.procedureId}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Procedure</option>

                                            {procedures.map((procedure) => {
                                                const id = procedure.ProcedureId || procedure.Id;
                                                const name = procedure.ProcedureName || procedure.Name;

                                                return (
                                                    <option key={id} value={id}>
                                                        {name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Appointment Date</label>
                                        <input
                                            type="date"
                                            name="appointmentDate"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.appointmentDate}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label fw-semibold text-muted">Notes</label>
                                        <textarea
                                            name="notes"
                                            className="form-control rounded-3 border-light bg-light"
                                            rows="2"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder="Reason for visit"
                                        ></textarea>
                                    </div>
                                </div>
                            </div>

                            {appointmentType === "NEW" && (
                                <div className="bg-white rounded-4 p-4 shadow-sm">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <i className="bi bi-heart-pulse text-danger fs-5"></i>
                                        <h6 className="fw-bold mb-0 text-dark">Medical History</h6>
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold text-muted">Allergy</label>
                                            <textarea
                                                name="allergy"
                                                className="form-control rounded-3 border-light bg-light"
                                                rows="2"
                                                value={formData.allergy}
                                                onChange={handleChange}
                                                placeholder="None"
                                            ></textarea>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold text-muted">Other Conditions</label>
                                            <textarea
                                                name="otherConditions"
                                                className="form-control rounded-3 border-light bg-light"
                                                rows="2"
                                                value={formData.otherConditions}
                                                onChange={handleChange}
                                                placeholder="Mention conditions"
                                            ></textarea>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label fw-semibold text-muted">Medications</label>
                                            <textarea
                                                name="medications"
                                                className="form-control rounded-3 border-light bg-light"
                                                rows="2"
                                                value={formData.medications}
                                                onChange={handleChange}
                                                placeholder="Current medications"
                                            ></textarea>
                                        </div>

                                        <div className="row align-items-end g-3">
                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold text-muted">Weight</label>
                                                <input
                                                    name="weight"
                                                    className="form-control form-control-sm rounded-3 border-light bg-light"
                                                    value={formData.weight}
                                                    onChange={handleChange}
                                                    placeholder="65 kgs"
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold text-muted">BP</label>
                                                <input
                                                    name="bp"
                                                    className="form-control form-control-sm rounded-3 border-light bg-light"
                                                    value={formData.bp}
                                                    onChange={handleChange}
                                                    placeholder="120/80"
                                                />
                                            </div>

                                            <div className="col-md-8">
                                                <div className="d-flex flex-wrap gap-3">

                                                    {[
                                                        ["diabetes", "Diabetes", "bi-droplet-half"],
                                                        ["epilepsy", "Epilepsy", "bi-activity"],
                                                        ["asthma", "Asthma", "bi-lungs"],
                                                    ].map(([name, label, icon]) => (
                                                        <label
                                                            key={name}
                                                            className={`border rounded-4 px-4 py-2 d-flex align-items-center gap-3 ${formData[name]
                                                                    ? "border-primary bg-primary bg-opacity-10"
                                                                    : "bg-light border-light"
                                                                }`}
                                                            style={{
                                                                cursor: "pointer",
                                                                minWidth: "170px",
                                                                height: "42px",
                                                            }}
                                                        >
                                                            <input
                                                                className="form-check-input m-0"
                                                                type="checkbox"
                                                                name={name}
                                                                checked={formData[name]}
                                                                onChange={handleChange}
                                                            />

                                                            <i
                                                                className={`bi ${icon} text-primary fs-5`}
                                                            ></i>

                                                            <span className="fw-semibold text-dark">
                                                                {label}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer border-0 bg-white px-4 py-3 flex-shrink-0">
                            <button
                                type="button"
                                className="btn btn-light fw-semibold rounded-3 px-4 py-2"
                                onClick={onClose}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary fw-bold rounded-3 px-4 py-2 shadow-sm"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-calendar2-check me-2"></i>
                                        {appointmentType === "NEW" ? "Make Appointment" : "Book Existing Patient"}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
